'use client';
// ct2 WhatsApp-style assistant "העוזר החכם של המומחים" (Founder 20/10/26, round 2 21/10/26) — replaces the bottom dock on category /
// category×city / topic / article pages. A chatbot, not a form: the conversation is ALREADY OPEN on load (minimise = stays minimised
// for the session), an OPEN first question about what the visitor searched (lib/assistantPrompts.ts, resolved on the server),
// common-answer chips + free typing, the city if unknown (ONE city door: setKnownCity), then the page's own pros as compact cards
// (same face as our WhatsApp offer card; same order as the page list = lib/proMatching cityPageOrder) and
// "קבלת הצעות בוואטסאפ" → consultHref(src:'assistant') = our WhatsApp door (ticket + Ads ids + 302). No timing question (21/10/26).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { setKnownCity, useSmartIntakeCtx, resolvePageCity } from '@/lib/smartIntakeStore';
import { detectCityInText, findCity } from '@/lib/israelCities';
import { consultHref } from '@/lib/consultLink.mjs';
import { getSessionId } from '@/lib/trackerSession';
import { fx, waHandoff } from '@/lib/funnelTrack';
import { cityPageOrder } from '@/lib/proMatching';
import CITIES from '@/lib/ct2Cities.json';
import type { AssistantPrompt } from '@/lib/assistantPrompts';
import type { AsstPro } from '@/lib/assistantPros';

type Msg = { id: number; from: 'bot' | 'me'; text: string; t: string; chips?: string[]; wa?: boolean; cards?: boolean };
type Step = 'q1' | 'city' | 'done';
const NAME = 'העוזר החכם של המומחים';
const MIN_KEY = 'ct2a_min';
const CARDS = 6;
const TOP6 = (CITIES as { he: string }[]).slice(0, 6).map(c => c.he);
const clock = () => { try { return new Intl.DateTimeFormat('he-IL', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Jerusalem' }).format(new Date()); } catch { return ''; } };
// the visitor's own words, short, for the acknowledgement ("הבנתי — סתימה בכיור.")
const echo = (s: string) => { const t = s.replace(/\s+/g, ' ').trim().replace(/[.!?,]+$/, ''); if (t.length <= 42) return t; const c = t.slice(0, 42); return c.slice(0, c.lastIndexOf(' ') > 20 ? c.lastIndexOf(' ') : 42) + '…'; };
const stars = (n: number) => (Math.round(n * 10) / 10).toFixed(1);
let seq = 0;

// the original WhatsApp glyph (brand mark), always on WhatsApp green #25D366
function WaIcon({ s = 22 }: { s?: number }) {
  return <svg aria-hidden="true" viewBox="0 0 24 24" width={s} height={s} fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" /></svg>;
}
function Avatar({ s = 40 }: { s?: number }) {
  return <span className="ct2a-av" style={{ width: s, height: s }} aria-hidden="true"><WaIcon s={Math.round(s * 0.58)} /></span>;
}

export default function Ct2Assistant({ prompt, catSlug, catName, topicSlug, cityName, pros = [], nationwide = false }: {
  prompt: AssistantPrompt; catSlug: string; catName: string; topicSlug?: string | null; cityName?: string | null; pros?: AsstPro[]; nationwide?: boolean;
}) {
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [typing, setTyping] = useState(false);
  const [step, setStep] = useState<Step>('q1');
  const [text, setText] = useState('');
  const [answers, setAnswers] = useState<string[]>([]);
  const [city, setCity] = useState<string | null>(null);
  const [cityMiss, setCityMiss] = useState(0);
  const intake = useSmartIntakeCtx();
  const pageCity = resolvePageCity(intake) || cityName || null;
  const inputRef = useRef<HTMLInputElement>(null);
  const launchRef = useRef<HTMLButtonElement>(null);
  const wallRef = useRef<HTMLDivElement>(null);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const started = useRef(false);
  const byUser = useRef(false); // focus moves only after a real open/minimise, never on the automatic first open

  const push = (m: Omit<Msg, 'id' | 't'>) => setMsgs(a => [...a, { ...m, id: ++seq, t: clock() }]);
  // bot lines arrive one by one, each after the typing dots (~600 ms)
  const bot = useCallback((lines: Omit<Msg, 'id' | 't' | 'from'>[]) => {
    let d = 0;
    lines.forEach((l, i) => {
      timers.current.push(setTimeout(() => setTyping(true), d));
      d += 650;
      timers.current.push(setTimeout(() => { push({ from: 'bot', ...l }); if (i === lines.length - 1) setTyping(false); }, d));
      d += 120;
    });
  }, []);
  const start = useCallback(() => {
    if (started.current) return;
    started.current = true;
    push({ from: 'bot', text: prompt.q1, chips: prompt.chips });
  }, [prompt]);

  // already open on load (Founder 21/10/26) unless minimised earlier in this session
  useEffect(() => {
    setMounted(true);
    let min = false; try { min = sessionStorage.getItem(MIN_KEY) === '1'; } catch {}
    start();
    if (!min) { setOpen(true); fx('assistant_open', { via: 'auto', cat: catSlug, topic: topicSlug || null }); }
    return () => { timers.current.forEach(clearTimeout); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const w = wallRef.current; if (w) w.scrollTop = w.scrollHeight; }, [msgs, typing, open]);
  useEffect(() => {
    if (!byUser.current) return;
    byUser.current = false;
    const f = setTimeout(() => (open ? inputRef.current : launchRef.current)?.focus({ preventScroll: true }), 60);
    return () => clearTimeout(f);
  }, [open]);

  const openChat = () => {
    byUser.current = true; start(); setOpen(true);
    try { sessionStorage.removeItem(MIN_KEY); } catch {}
    fx('assistant_open', { via: 'launcher', cat: catSlug, topic: topicSlug || null });
  };
  const minimise = () => { byUser.current = true; setOpen(false); try { sessionStorage.setItem(MIN_KEY, '1'); } catch {} };

  const knownCity = city || pageCity;
  // the page's own pros, in the page's own order for the known city (lib/proMatching cityPageOrder — the list's pipeline)
  const cityCoords = useMemo(() => { const c = knownCity ? findCity(knownCity) : null; return c ? { lat: c.lat, lng: c.lng } : null; }, [knownCity]);
  const shownPros = useMemo(() => {
    if (!pros.length) return [] as AsstPro[];
    try { return (cityPageOrder(pros as any[], knownCity, cityCoords, { nationwide }).list as AsstPro[]).slice(0, CARDS); } catch { return pros.slice(0, CARDS); }
  }, [pros, knownCity, cityCoords, nationwide]);

  const cityQ = () => ({ text: 'באיזו עיר או באיזה אזור?', chips: TOP6 });
  const close = (ans: string[], c: string | null) => {
    const [a1, ...more] = ans;
    const where = c ? ` ב${c}` : '';
    return [
      { text: `הבנתי — ${echo(a1 || '')}${more.length ? ` · ${more.map(echo).join(' · ')}` : ''}${where}.` },
      ...(shownPros.length ? [{ text: `המומחים המובילים${c ? ` בסביבת ${c}` : ''}:`, cards: true }] : []),
      { text: 'אפשר לקבל מהם הצעות ישירות בוואטסאפ שלנו.', wa: true },
    ];
  };
  const pickCity = (raw: string): string | null => {
    const hit = detectCityInText(raw)?.city || findCity(raw.trim());
    if (!hit) return null;
    setKnownCity(hit.name, { lat: hit.lat, lng: hit.lng });
    setCity(hit.name);
    return hit.name;
  };

  function send(raw: string, viaChip = false) {
    const v = raw.replace(/\s+/g, ' ').trim().slice(0, 200);
    if (!v || typing) return;
    push({ from: 'me', text: v });
    setText('');
    fx('assistant_answer', { step, chip: viaChip, cat: catSlug });
    if (step === 'q1') {
      // a city typed inside the first answer counts — never asked twice
      const det = detectCityInText(v);
      const c = det ? pickCity(v) : null;
      const need = det && det.rest.length >= 2 ? det.rest : v;
      const ans = [need];
      setAnswers(ans);
      const have = c || knownCity;
      if (!have && !nationwide) { setStep('city'); bot([{ text: `הבנתי — ${echo(need)}.` }, cityQ()]); return; }
      setStep('done'); bot(close(ans, have || null));
      return;
    }
    if (step === 'city') {
      const c = pickCity(v);
      if (!c) {
        if (cityMiss >= 1) { setCityMiss(0); setStep('done'); bot([{ text: 'בסדר, את האזור נשלים בוואטסאפ.' }, ...close(answers, null).slice(1)]); return; }
        setCityMiss(n => n + 1); bot([{ text: 'לא הצלחתי לזהות את העיר. אפשר לכתוב את שם העיר או הישוב?', chips: TOP6 }]); return;
      }
      // "נזילה מתחת לכיור בכפר סבא" answered here: the words besides the city are part of the request too
      const rest = detectCityInText(v)?.rest || '';
      const ans = rest.length >= 6 ? [...answers, rest] : answers;
      setAnswers(ans); setStep('done');
      bot(close(ans, c));
      return;
    }
    // done: anything more is added to the request (a city named now moves the cards too)
    const c2 = detectCityInText(v) ? pickCity(v) : null;
    const rest = c2 ? (detectCityInText(v)?.rest || '') : v;
    const ans = rest.length >= 2 ? [...answers, rest].slice(-4) : answers;
    setAnswers(ans);
    bot([{ text: c2 ? `עדכנתי את האזור: ${c2}.` : 'הוספתי לבקשה.' }, { text: 'אפשר לקבל הצעות ישירות בוואטסאפ שלנו.', wa: true }]);
  }

  const sid = (() => { try { return mounted ? getSessionId() : null; } catch { return null; } })();
  const need = answers.join(' · ').slice(0, 160);
  const href = consultHref({ cat: catSlug, topic: topicSlug || null, city: knownCity || null, need: need || null, sid, src: 'assistant' });
  const lastBot = [...msgs].reverse().find(m => m.from === 'bot');
  const lastWa = [...msgs].reverse().find(m => m.wa);
  const lastCards = [...msgs].reverse().find(m => m.cards);
  const onWa = () => { fx('assistant_wa', { cat: catSlug, city: knownCity || null, n: answers.length }); waHandoff('assistant', { cat: catSlug }); };

  if (!mounted) return null;
  return (
    <div className="ct2a" dir="rtl">
      <style>{CT2A_CSS}</style>
      {!open && (
        <button ref={launchRef} type="button" className="ct2a-launch" onClick={openChat} aria-haspopup="dialog" aria-expanded={false} aria-label={`פתיחת צ'אט — ${NAME}`}>
          <Avatar s={40} />
          <span className="ct2a-lt"><b>{NAME}</b><span>{lastBot && step !== 'q1' ? lastBot.text : prompt.q1}</span></span>
          <span className="ct2a-lgo" aria-hidden="true"><svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M6 15l6-6 6 6" /></svg></span>
        </button>
      )}
      {open && (
        <div className="ct2a-win" role="dialog" aria-modal="false" aria-label={NAME} onKeyDown={e => { if (e.key === 'Escape') { e.stopPropagation(); minimise(); } }}>
          <div className="ct2a-head">
            <Avatar s={36} />
            <div className="ct2a-ht"><b>{NAME}</b><span>{typing ? 'מקליד…' : 'עונה מיד'}</span></div>
            <button type="button" className="ct2a-x" onClick={minimise} aria-label="מזעור הצ'אט">
              <svg aria-hidden="true" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
            </button>
          </div>
          <div className="ct2a-wall" ref={wallRef} aria-live="polite">
            {msgs.map(m => (
              <div key={m.id} className={`ct2a-row ${m.from}`}>
                <div className={`ct2a-b ${m.from}`}>
                  <span className="ct2a-bt">{m.text}</span>
                  <span className="ct2a-time">{m.t}{m.from === 'me' && <svg aria-label="נקרא" viewBox="0 0 16 11" width="16" height="11" fill="#53bdeb"><path d="M11 .6 4.9 7.7 2.3 5.3l-.9 1 3.6 3.3L12 1.5zM15 .6 8.9 7.7l-.6-.5-.9 1 1.6 1.4L16 1.5z" /></svg>}</span>
                </div>
                {m.cards && m === lastCards && (
                  <div className="ct2a-cards" role="list" aria-label="מומחים">
                    {shownPros.map(p => (
                      <a key={p.id} role="listitem" className="ct2a-card" href={p.href} onClick={() => fx('assistant_card', { cat: catSlug, pro: p.id })}>
                        <b>{p.firstName}</b>
                        {p.rating10 > 0 && <span className="ct2a-cr"><svg aria-hidden="true" viewBox="0 0 24 24" width="13" height="13" fill="#FBBC04"><path d="M12 2.5l2.9 6.1 6.6.8-4.9 4.6 1.3 6.6L12 17.3l-5.9 3.3 1.3-6.6-4.9-4.6 6.6-.8z" /></svg><bdi dir="ltr">{stars(p.rating10)}</bdi>{p.reviewCount > 0 && <span> · {p.reviewCount} המלצות</span>}</span>}
                        {p.locationStr && <span className="ct2a-cl">{p.locationStr}</span>}
                      </a>
                    ))}
                  </div>
                )}
                {m.wa && m === lastWa && (
                  <a className="ct2a-wa" href={href} onClick={onWa}><WaIcon s={22} /> קבלת הצעות בוואטסאפ</a>
                )}
                {m.chips && m === lastBot && !typing && (
                  <div className="ct2a-chips">
                    {m.chips.map(c => <button key={c} type="button" className="ct2a-chip" onClick={() => send(c, true)}>{c}</button>)}
                  </div>
                )}
              </div>
            ))}
            {typing && <div className="ct2a-row bot"><div className="ct2a-b bot ct2a-dots" aria-label="מקליד"><i /><i /><i /></div></div>}
          </div>
          <form className="ct2a-bar" onSubmit={e => { e.preventDefault(); send(text); }}>
            <input ref={inputRef} className="ct2a-in" value={text} onChange={e => setText(e.target.value)} placeholder="הקלדת הודעה" aria-label="הקלדת הודעה" enterKeyHint="send" maxLength={200} autoComplete="off" />
            <button type="submit" className="ct2a-send" aria-label="שליחה" disabled={!text.trim() || typing}>
              <svg aria-hidden="true" viewBox="0 0 24 24" width="22" height="22" fill="currentColor" style={{ transform: 'scaleX(-1)' }}><path d="M2 21 23 12 2 3v7l15 2-15 2z" /></svg>
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

// WhatsApp palette: header #008069, wall #efeae2, incoming white, outgoing #d9fdd3, brand #25D366. Open by default and LOW (Founder 21/10/26:
// "שהגובה של השיחה יהיה יותר נמוך כדי שיראו יותר מהאתר") — mobile ≤45% of the viewport, no backdrop, the page stays scrollable;
// desktop ≥1024 = a compact corner panel at the bottom-LEFT (the menu drawer opens from the right). Chips + cards scroll sideways.
// Founder 25/10/26: no env(safe-area-inset-bottom) on the bottom offsets — Chrome Android edge-to-edge reports the nav-bar inset without viewport-fit=cover and lifted the panel ~48px.
const CT2A_CSS = `
.ct2a{font-family:inherit}
.ct2a button{font-family:inherit}
.ct2a-av{display:grid;place-items:center;flex:none;border-radius:50%;background:#25D366;color:#fff}
.ct2a-launch{position:fixed;z-index:55;inset-inline:12px;bottom:10px;max-width:520px;margin-inline:auto;display:flex;align-items:center;gap:10px;min-height:62px;padding:8px 10px;background:#fff;border:1px solid #e3e6f2;border-radius:22px;box-shadow:0 10px 30px rgba(4,1,84,.22);cursor:pointer;text-align:start;color:#111b21}
.ct2a-lt{flex:1;min-width:0;display:flex;flex-direction:column;gap:2px;line-height:1.2}.ct2a-lt b{font-size:15px;font-weight:800}.ct2a-lt span{font-size:13px;color:#54656f;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ct2a-lgo{flex:none;width:44px;height:44px;border-radius:50%;display:grid;place-items:center;background:#f0f2f5;color:#008069}
.ct2a-chips{display:flex;gap:6px;margin:6px 0 2px;max-width:100%;overflow-x:auto;scrollbar-width:none;padding-bottom:2px}
.ct2a-chips::-webkit-scrollbar,.ct2a-cards::-webkit-scrollbar{display:none}
.ct2a-chip{flex:none;min-height:40px;padding:0 14px;border:1px solid #d1d7db;border-radius:20px;background:#fff;color:#008069;font-size:14.5px;font-weight:700;white-space:nowrap;cursor:pointer;box-shadow:0 1px .5px rgba(11,20,26,.13)}
.ct2a-chip:hover{background:#f0f2f5}
.ct2a-win{position:fixed;z-index:56;inset-inline:8px;bottom:8px;max-height:min(45dvh,400px);display:flex;flex-direction:column;background:#efeae2;border-radius:16px;overflow:hidden;box-shadow:0 8px 32px rgba(11,20,26,.32);animation:ct2aUp .28s cubic-bezier(.2,.8,.2,1) both}
.ct2a-head{flex:none;display:flex;align-items:center;gap:10px;padding:6px 8px;background:#008069;color:#fff}
.ct2a-ht{flex:1;min-width:0;display:flex;flex-direction:column;line-height:1.2}.ct2a-ht b{font-size:15px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.ct2a-ht span{font-size:12.5px;opacity:.88}
.ct2a-x{flex:none;width:44px;height:44px;border:0;border-radius:50%;display:grid;place-items:center;background:rgba(255,255,255,.12);color:#fff;cursor:pointer}
.ct2a-wall{flex:1 1 auto;min-height:0;overflow-y:auto;overscroll-behavior:contain;padding:10px 10px 6px;display:flex;flex-direction:column;gap:5px;background-color:#efeae2;background-image:radial-gradient(rgba(11,20,26,.045) 1px,transparent 1px);background-size:16px 16px}
.ct2a-row{display:flex;flex-direction:column;min-width:0}
.ct2a-row.bot{align-items:flex-start}.ct2a-row.me{align-items:flex-end}
.ct2a-b{position:relative;max-width:86%;padding:5px 9px;border-radius:8px;font-size:15px;line-height:1.4;color:#111b21;box-shadow:0 1px .5px rgba(11,20,26,.13);animation:ct2aPop .2s ease-out both}
.ct2a-b.bot{background:#fff;border-start-start-radius:0}
.ct2a-b.me{background:#d9fdd3;border-start-end-radius:0}
.ct2a-b.bot::before,.ct2a-b.me::before{content:"";position:absolute;top:0;width:8px;height:13px}
.ct2a-b.bot::before{inset-inline-start:-8px;background:#fff;clip-path:polygon(0 0,100% 0,0 100%)}
.ct2a-b.me::before{inset-inline-end:-8px;background:#d9fdd3;clip-path:polygon(0 0,100% 0,100% 100%)}
.ct2a-bt{white-space:pre-wrap;overflow-wrap:anywhere}
.ct2a-time{float:inline-end;display:inline-flex;align-items:center;gap:3px;margin-inline-start:10px;margin-top:5px;font-size:11px;color:#667781;line-height:1}
.ct2a-dots{display:inline-flex;gap:4px;padding:11px 14px}.ct2a-dots i{width:7px;height:7px;border-radius:50%;background:#8696a0;animation:ct2aDot 1s infinite}.ct2a-dots i:nth-child(2){animation-delay:.15s}.ct2a-dots i:nth-child(3){animation-delay:.3s}
.ct2a-cards{display:flex;gap:8px;max-width:100%;margin:6px 0 2px;overflow-x:auto;scroll-snap-type:x mandatory;scrollbar-width:none;padding-bottom:2px}
.ct2a-card{flex:none;width:136px;padding-top:6px;scroll-snap-align:start;display:flex;flex-direction:column;gap:2px;padding-bottom:8px;background:#fff;border-radius:10px;overflow:hidden;box-shadow:0 1px 2px rgba(11,20,26,.18);color:#111b21;text-decoration:none}
.ct2a-card img{display:block;width:136px;height:96px;object-fit:cover;object-position:50% 25%;background:#dfe5e7}
.ct2a-card b{padding:4px 8px 0;font-size:14px;line-height:1.25;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ct2a-cr{display:flex;align-items:center;gap:3px;padding:0 8px;font-size:12.5px;font-weight:700;color:#3b4a54;white-space:nowrap}.ct2a-cr span{font-weight:500;color:#54656f}
.ct2a-cl{padding:0 8px;font-size:12px;color:#667781;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ct2a-wa{display:inline-flex;align-items:center;justify-content:center;gap:8px;margin-top:6px;min-height:46px;padding:0 20px;border-radius:24px;background:#25D366;color:#fff;font-size:16px;font-weight:800;text-decoration:none;box-shadow:0 4px 14px rgba(37,211,102,.4)}
.ct2a-wa:hover{background:#1ebe5b}
.ct2a-bar{flex:none;display:flex;align-items:center;gap:8px;padding:6px 8px;background:#f0f2f5}
.ct2a-in{flex:1;min-width:0;min-height:44px;padding:0 16px;border:0;border-radius:22px;background:#fff;font:inherit;font-size:16px;color:#111b21}
.ct2a-in:focus{outline:2px solid #00a884;outline-offset:0}
.ct2a-send{flex:none;width:44px;height:44px;border:0;border-radius:50%;display:grid;place-items:center;background:#00a884;color:#fff;cursor:pointer}
.ct2a-send:disabled{background:#8fd9c6;cursor:default}
.ct2a-launch:focus-visible,.ct2a-chip:focus-visible,.ct2a-x:focus-visible,.ct2a-send:focus-visible,.ct2a-wa:focus-visible,.ct2a-card:focus-visible{outline:3px solid #ff3f3f;outline-offset:2px}
@keyframes ct2aUp{from{transform:translateY(40px);opacity:0}to{transform:none;opacity:1}}
@keyframes ct2aPop{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:none}}
@keyframes ct2aDot{0%,60%,100%{opacity:.35;transform:none}30%{opacity:1;transform:translateY(-3px)}}
@media (min-width:1024px){
.ct2a-launch{inset-inline:auto;inset-inline-end:24px;bottom:24px;width:64px;height:64px;min-height:0;padding:0;border-radius:50%;justify-content:center;background:#25D366;border:0;box-shadow:0 8px 24px rgba(11,20,26,.28)}
.ct2a-launch .ct2a-lt,.ct2a-launch .ct2a-lgo{display:none}
.ct2a-launch .ct2a-av{width:64px!important;height:64px!important;background:none}.ct2a-launch .ct2a-av svg{width:34px;height:34px}
.ct2a-win{inset-inline:auto;inset-inline-end:24px;bottom:24px;width:360px;max-height:min(460px,calc(100vh - 120px))}
}
@media (prefers-reduced-motion:reduce){.ct2a-win,.ct2a-b,.ct2a-dots i{animation:none}}
`;
